<template>
    <Head :title="t('assessments.selfAssess.pageTitle')" />
    <BaseLayout>
        <BaseButton v-if="!usesPagedRenderer && !showingIntro && !submittedAt" :disabled="composerBusy" theme="tertiary" size="sm" class="self-start" @click="goBackToCycle">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.selfAssess.back") }}
        </BaseButton>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.selfAssess.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <!-- A submitted response is final, so the taker never renders for one.
             The backend refuses the edit anyway; this is so nobody is handed a
             form whose saves will fail. -->
        <BaseCard v-else-if="submittedAt" padding="lg" class="flex flex-col items-start gap-3">
            <StatusChip state="done">{{ t("assessments.selfAssess.complete") }}</StatusChip>
            <div class="body-small-bold text-ink">{{ t("assessments.selfAssess.submittedOn", { date: formatDate(submittedAt) }) }}</div>
            <div class="body-small text-ink-muted">{{ t("assessments.selfAssess.answersFinal") }}</div>
            <a :href="`/reflections/360/${props.cycle_id}`" class="base-btn-neutral base-btn-sm">{{ t("assessments.selfAssess.backToCycle") }}</a>
        </BaseCard>
        <AssessmentIntroLanding v-else-if="showingIntro && intro" :intro="intro" @begin="dismissIntro" />
        <AssessmentGroupedSections
            v-else-if="usesPagedRenderer"
            ref="groupedSections"
            :response-id="responseId"
            :questions="questions"
            :sections="sections"
            :display-mode="displayMode"
            :questions-per-page="questionsPerPage"
            :allow-score-examples="allowScoreExamples"
            :allow-not-observed="allowNotObserved"
            :accent-color="ACCENT_SELF"
            :accent-surface-color="ACCENT_SELF_SURFACE"
            @submitted="onGroupedSubmitted"
            @exit="exitToCycle" />
        <template v-else-if="currentQuestion">
            <header>
                <div v-if="currentTopSection" class="flex items-baseline justify-between gap-3 mb-3">
                    <h1 class="body-bold text-ink leading-tight">{{ currentTopSection.title }}</h1>
                    <AssessmentEyebrow v-if="currentChildSection">{{ currentChildSection.title }}</AssessmentEyebrow>
                </div>
                <div class="flex items-center justify-between details font-medium text-ink-muted mb-3">
                    <AssessmentEyebrow>{{ t("assessments.selfAssess.yourTurn") }}</AssessmentEyebrow>
                    <span>{{ headerStatus }}</span>
                </div>
                <BaseProgressBar :value="progressPct" size="xs" :aria-label="t('assessments.selfAssess.progress', { percent: progressPct })" :color="ACCENT_SELF" />
            </header>

            <SectionIntroCard v-if="showingSectionIntro && currentTopSection" :section="currentTopSection" :question-count="currentTopSectionQuestionCount" />

            <QuestionCard
                v-else
                :question="currentQuestion"
                :score="currentSavedScore"
                :text="currentTextDraft"
                :not-observed="currentSavedNotObserved"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isCurrentUnanswered"
                :accent-color="ACCENT_SELF"
                :accent-surface-color="ACCENT_SELF_SURFACE"
                @update:score="setScore"
                @update:text="currentTextDraft = $event"
                @update:not-observed="setNotObserved"
                @update:busy="composerBusy = $event" />

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div class="flex items-center justify-between">
                <BaseButton :disabled="(currentIndex === 0 && !showingSectionIntro) || saving || composerBusy" theme="tertiary" @click="goPrev">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.selfAssess.previous") }}
                </BaseButton>

                <BaseButton v-if="showingSectionIntro" theme="primary" @click="dismissSectionIntro">{{ t("assessments.selfAssess.beginSection") }}</BaseButton>
                <BaseButton v-else-if="!isLast" theme="primary" :disabled="saving || composerBusy" @click="goNext">{{ t("assessments.selfAssess.next") }}</BaseButton>
                <BaseButton v-else theme="primary" :disabled="submitting || submitted || saving || composerBusy" @click="submit">
                    {{ submitting ? t("assessments.selfAssess.submitting") : t("assessments.selfAssess.submit") }}
                </BaseButton>
            </div>
            <div v-if="composerBusy && !showingSectionIntro" class="details text-ink-muted text-right -mt-3">{{ t("assessments.selfAssess.stopRecording") }}</div>
        </template>
    </BaseLayout>
    <!-- A submitted response is never re-filled, but staff can still create a fresh one to demo. -->
    <AssessmentDemoTools
        v-if="responseId"
        :response-actions="submittedAt ? [] : [{ id: responseId, label: 'Self-assessment' }]"
        bootstrap-kind="self"
        :before-fill="saveDrafts"
        @filled="onFilled" />
    <ConfirmationDialog
        v-model:open="showSubmitConfirm"
        :title="t('assessments.selfAssess.submitConfirmTitle')"
        :confirm-label="t('assessments.selfAssess.submitConfirmLabel')"
        :cancel-label="t('assessments.selfAssess.keepEditing')"
        :confirm-disabled="submitting"
        :close-on-confirm="false"
        @confirm="confirmSubmit">
        <template #content>
            <I18nT keypath="assessments.selfAssess.submitConfirmBody" tag="div" class="body text-ink-muted">
                <template #emphasis>
                    <strong class="text-ink">{{ t("assessments.selfAssess.submitConfirmEmphasis") }}</strong>
                </template>
            </I18nT>
        </template>
    </ConfirmationDialog>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, AssessmentIntro, QuestionResponseData, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import AssessmentIntroLanding from "~vue/components/AssessmentIntroLanding.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import SectionIntroCard from "~vue/components/SectionIntroCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { DEFAULT_QUESTIONS_PER_PAGE, rendersQuestionPages } from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { formatDate, isNetworkError } from "~vue/utils/threeSixty";
import { computed, nextTick, onMounted, ref, useTemplateRef } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    cycle_id: string;
}>();

// The self-reflection amber, matching the hub's version of this same screen
// (pages/reflections/SelfAssessTaker.vue). Without an accent the shared
// question components fall back to neutral, and this page used to read purple.
// Orange is the ledger's self-reflect family and its pending status, so sharing
// the status token is intentional rather than a near-enough match.
const ACCENT_SELF = "var(--color-status-warning)";
const ACCENT_SELF_SURFACE = "var(--color-chip-self-reflect-bg)";

const groupedSections = useTemplateRef("groupedSections");
const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const questionsPerPage = ref(DEFAULT_QUESTIONS_PER_PAGE);
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const usesPagedRenderer = computed(() => rendersQuestionPages(displayMode.value));
const intro = ref<AssessmentIntro | null>(null);
const showingIntro = ref(false);
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const showSubmitConfirm = ref(false);
const submittedAt = ref<string | null>(null);
// Latched once the response is submitted. The Inertia visit below unmounts this
// page, but only eventually — until then this keeps the dialog and the button
// from starting a second submit.
const submitted = ref(false);
const showingSectionIntro = ref(false);
// Mirrors the composer's recording/transcribing state so navigation buttons
// can be disabled while a recording is in flight (LAB-491).
const composerBusy = ref(false);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/self-response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || t("assessments.selfAssess.loadError");
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    displayMode.value = payload.display_mode ?? "one_per_page";
    questionsPerPage.value = payload.questions_per_page ?? DEFAULT_QUESTIONS_PER_PAGE;
    intro.value = payload.intro ?? null;
    submittedAt.value = payload.submitted_at ?? null;
    // Only show the intro on a fresh (unsubmitted) response so returning
    // participants aren't gated by it again.
    showingIntro.value = intro.value !== null && !payload.submitted_at;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
    if (shouldShowIntroFor(0)) {
        showingSectionIntro.value = true;
    }
    const answeredCount = payload.questions.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_VIEWED, {
        cycle_id: props.cycle_id,
        total_questions: payload.questions.length,
        answered_count: answeredCount,
    });
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const progressPct = computed(() => Math.round(((currentIndex.value + 1) / questions.value.length) * 100));

const sectionsByCode = computed(() => {
    const map: Record<string, SectionSummary> = {};
    for (const s of sections.value) {
        map[s.code] = s;
    }
    return map;
});

// Walk up parent_code chain to the top-level (depth=0) ancestor.
function topSectionForCode(code: string | null | undefined): SectionSummary | null {
    if (!code) {
        return null;
    }
    let current = sectionsByCode.value[code] ?? null;
    while (current && current.parent_code) {
        const parent = sectionsByCode.value[current.parent_code];
        if (!parent) {
            break;
        }
        current = parent;
    }
    return current ?? null;
}

const currentTopSection = computed<SectionSummary | null>(() => topSectionForCode(currentQuestion.value?.section_code));

const currentChildSection = computed<SectionSummary | null>(() => {
    const code = currentQuestion.value?.section_code;
    if (!code) {
        return null;
    }
    const direct = sectionsByCode.value[code] ?? null;
    if (!direct || !direct.parent_code) {
        return null;
    }
    return direct;
});

const currentTopSectionQuestionCount = computed<number>(() => {
    const topCode = currentTopSection.value?.code;
    if (!topCode) {
        return 0;
    }
    return questions.value.filter((q) => topSectionForCode(q.section_code)?.code === topCode).length;
});

const headerStatus = computed(() => {
    if (showingSectionIntro.value) {
        return t("assessments.selfAssess.aboutToStartSection");
    }
    return t("assessments.selfAssess.questionOf", { current: currentIndex.value + 1, total: questions.value.length });
});

function shouldShowIntroFor(index: number): boolean {
    const q = questions.value[index];
    if (!q || !q.section_code) {
        return false;
    }
    const topCode = topSectionForCode(q.section_code)?.code;
    if (!topCode) {
        return false;
    }
    if (index === 0) {
        return true;
    }
    const prev = questions.value[index - 1];
    const prevTopCode = topSectionForCode(prev?.section_code)?.code;
    return prevTopCode !== topCode;
}

function dismissSectionIntro() {
    showingSectionIntro.value = false;
}

function dismissIntro() {
    showingIntro.value = false;
}

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}

function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = t("assessments.selfAssess.saveFailed");
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

/** Sync the current text draft and validate the current question. Returns true if valid. */
function validateCurrentQuestion(action: "continuing" | "submitting"): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        error.value = action === "continuing" ? t("assessments.selfAssess.requiredBeforeContinuing") : t("assessments.selfAssess.requiredBeforeSubmitting");
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (currentIndex.value >= questions.value.length - 1) {
        return;
    }
    if (!validateCurrentQuestion("continuing")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value += 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showingSectionIntro.value = shouldShowIntroFor(currentIndex.value);
}

async function goPrev() {
    // If we're on the intro for the current section, "Previous" just dismisses
    // the intro and shows the section's first question — nothing to save.
    if (showingSectionIntro.value) {
        showingSectionIntro.value = false;
        return;
    }
    if (currentIndex.value === 0) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showingSectionIntro.value = false;
}

async function goBackToCycle() {
    if (!(await saveCurrent())) {
        return;
    }
    router.visit(`/reflections/360/${props.cycle_id}`);
}

const showValidation = ref(false);

const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

async function saveDrafts(): Promise<boolean> {
    if (usesPagedRenderer.value) {
        return (await groupedSections.value?.saveDirtyOnPage()) ?? true;
    }
    return saveCurrent();
}

async function onFilled(filled: QuestionResponseData[]) {
    const byQuestionId = new Map(filled.map((answer) => [answer.question_id, answer]));
    for (const question of questions.value) {
        const answer = byQuestionId.get(question.id);
        if (answer) {
            question.saved = { score: answer.score ?? null, text: answer.text, not_observed: answer.not_observed };
        }
    }
    showingSectionIntro.value = false;
    currentIndex.value = Math.max(questions.value.length - 1, 0);
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showValidation.value = false;
    error.value = null;
    // The paged renderer only mounts once the intro landing is dismissed.
    showingIntro.value = false;
    await nextTick();
    groupedSections.value?.adoptSavedAnswers([...byQuestionId.keys()]);
    // Land on the last step so the filled assessment is one click from Submit.
    groupedSections.value?.goToLastPage();
}

async function submit() {
    if (submitting.value || submitted.value) {
        return;
    }
    if (!validateCurrentQuestion("submitting")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value || submitted.value) {
        return;
    }
    submitting.value = true;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            error.value = isNetworkError(fetchError.value)
                ? t("assessments.selfAssess.connectionSubmitFailed")
                : (data.value as { error?: string })?.error || t("assessments.selfAssess.submitFailed");
            return;
        }
        submitted.value = true;
        const answeredCount = questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
            cycle_id: props.cycle_id,
            total_questions: questions.value.length,
            answered_count: answeredCount,
        });
        router.visit(`/reflections/360/${props.cycle_id}`);
    } finally {
        submitting.value = false;
        // The dialog is `:close-on-confirm="false"`, so it stays up until we
        // close it — a slow, cancelled or failed visit must not leave it open.
        showSubmitConfirm.value = false;
    }
}

// Grouped renderer owns its own save/submit; the host keeps the submit
// analytics + redirect so behavior matches the one-per-page flow.
function onGroupedSubmitted() {
    const answeredCount = questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
        cycle_id: props.cycle_id,
        total_questions: questions.value.length,
        answered_count: answeredCount,
    });
    exitToCycle();
}

// The grouped renderer saves dirty answers before emitting `exit`, so the host
// only needs to redirect (no extra save like goBackToCycle does).
function exitToCycle() {
    router.visit(`/reflections/360/${props.cycle_id}`);
}
</script>
